import { useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Login + Sign up + Forgot password — single surface, three modes.
//
// Phase 2C: self-serve sign-up. New customers can create their own
// account from the marketing site (or directly here). On submit,
// the handle_new_user Postgres trigger materializes restaurant +
// restaurant_members + settings rows so the app is usable from the
// first signed-in render.
//
// Email + password chosen over magic links because pilot users may
// not have inbox access during a busy shift.
export default function LoginScreen() {
  const [mode, setMode] = useState('signin')   // 'signin' | 'signup' | 'forgot'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [restaurantName, setRestaurantName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [info, setInfo] = useState(null)        // success / "check your email" message

  function switchMode(next) {
    setMode(next)
    setError(null)
    setInfo(null)
  }

  // ---------- Sign in ----------
  async function handleSignIn(e) {
    e?.preventDefault?.()
    if (submitting) return
    if (!email.trim() || !password) {
      setError('Enter your email and password.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const { error: authErr } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      })
      if (authErr) throw authErr
      // useSession picks up the new session and the App shell flips.
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[LoginScreen] sign-in failed', err)
      const msg = err?.message?.toLowerCase().includes('invalid')
        ? 'Email or password is incorrect.'
        : err?.message?.toLowerCase().includes('confirm')
          ? 'Please confirm your email address before signing in.'
          : 'Could not sign in. Check your connection and try again.'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  // ---------- Sign up ----------
  // restaurant_name flows into raw_user_meta_data and the
  // handle_new_user trigger uses it to seed the restaurant row.
  async function handleSignUp(e) {
    e?.preventDefault?.()
    if (submitting) return
    if (!email.trim() || !password || !restaurantName.trim()) {
      setError('Fill in your email, password, and restaurant name.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    setSubmitting(true)
    setError(null)
    setInfo(null)
    try {
      // Detect browser timezone so the restaurant row gets it right
      // automatically. Falls back to America/Chicago if unavailable.
      const tz = (() => {
        try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null }
        catch { return null }
      })()

      const { data, error: authErr } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            restaurant_name: restaurantName.trim(),
            timezone: tz,
          },
        },
      })
      if (authErr) throw authErr

      // Two paths:
      //   - Email confirmation enabled: data.session is null, user
      //     must confirm via email before signing in.
      //   - Email confirmation disabled: data.session is present and
      //     useSession will flip the app shell automatically.
      if (!data.session) {
        setInfo(
          `Account created — check ${email.trim()} for a confirmation link, then come back to sign in.`
        )
        setPassword('')   // don't keep the password lying around
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[LoginScreen] sign-up failed', err)
      const m = err?.message?.toLowerCase() || ''
      const msg = m.includes('already registered') || m.includes('already exists')
        ? 'That email is already registered. Try signing in instead.'
        : m.includes('password')
          ? 'Password is too weak. Use at least 8 characters.'
          : `Could not create account: ${err?.message || 'try again.'}`
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  // ---------- Forgot password ----------
  async function handleForgot(e) {
    e?.preventDefault?.()
    if (submitting) return
    if (!email.trim()) {
      setError('Enter your email so we can send the reset link.')
      return
    }
    setSubmitting(true)
    setError(null)
    setInfo(null)
    try {
      const redirectTo = `${window.location.origin}/`
      const { error: rstErr } = await supabase.auth.resetPasswordForEmail(
        email.trim(),
        { redirectTo },
      )
      if (rstErr) throw rstErr
      setInfo(
        `If an account exists for ${email.trim()}, we just sent a reset link. Open it from the same device to set a new password.`
      )
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[LoginScreen] reset failed', err)
      setError('Could not send reset email. Try again in a minute.')
    } finally {
      setSubmitting(false)
    }
  }

  const headerLabel =
    mode === 'signin' ? 'Sign in' :
    mode === 'signup' ? 'Create account' :
    'Reset password'

  return (
    <div className="min-h-full bg-white">
      <header className="bg-amber-brand-50 text-amber-brand-800 safe-top">
        <div className="mx-auto max-w-screen-sm px-4 py-4 flex items-center gap-3">
          <img
            src="/logo.png"
            alt="AI Restaurant Manager"
            className="h-12 w-12 rounded-xl shadow-md flex-shrink-0"
          />
          <div className="min-w-0">
            <div className="text-xs font-medium opacity-90">
              AI Restaurant Manager
            </div>
            <div className="text-base font-medium">{headerLabel}</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-screen-sm px-4 pt-6 pb-8">
        {/* Mode toggle (sign in / sign up). Hidden when on the forgot
            screen since that's a sub-flow off Sign in. */}
        {mode !== 'forgot' && (
          <div className="mb-4 grid grid-cols-2 gap-1 rounded-lg border border-black/10 p-1 bg-black/[0.02]">
            <button
              type="button"
              onClick={() => switchMode('signin')}
              className={`rounded-md px-3 py-2 text-sm font-medium ${
                mode === 'signin' ? 'bg-white text-blue-700 shadow-sm' : 'text-black/55'
              }`}
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => switchMode('signup')}
              className={`rounded-md px-3 py-2 text-sm font-medium ${
                mode === 'signup' ? 'bg-white text-blue-700 shadow-sm' : 'text-black/55'
              }`}
            >
              Create account
            </button>
          </div>
        )}

        {error && (
          <div role="alert" className="mb-3 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
            {error}
          </div>
        )}
        {info && (
          <div role="status" className="mb-3 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow">
            {info}
          </div>
        )}

        {mode === 'signin' && (
          <form onSubmit={handleSignIn} className="space-y-4">
            <p className="text-sm leading-relaxed text-black/70">
              Welcome back. Sign in with the email and password for your kitchen.
            </p>
            <FieldEmail value={email} onChange={setEmail} />
            <FieldPassword
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              label="Password"
            />
            <button
              type="submit"
              disabled={submitting}
              className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Signing in\u2026' : 'Sign in'}
            </button>
            <button
              type="button"
              onClick={() => switchMode('forgot')}
              className="block w-full text-center text-sm font-medium text-blue-700 hover:underline"
            >
              Forgot password?
            </button>
          </form>
        )}

        {mode === 'signup' && (
          <form onSubmit={handleSignUp} className="space-y-4">
            <p className="text-sm leading-relaxed text-black/70">
              Create a new kitchen. We'll set up your account and a starting
              restaurant in one step. You can refine the details after you sign in.
            </p>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="signup-restaurant">
                Restaurant name
              </label>
              <input
                id="signup-restaurant"
                type="text"
                value={restaurantName}
                onChange={(e) => setRestaurantName(e.target.value)}
                placeholder='e.g. "I Smell Bacon BBQ"'
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
            <FieldEmail value={email} onChange={setEmail} />
            <FieldPassword
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              label="Password (8+ characters)"
            />
            <button
              type="submit"
              disabled={submitting}
              className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Creating account\u2026' : 'Create account'}
            </button>
            <p className="text-center text-xs text-black/55">
              By creating an account you agree to use the app honestly with
              your real restaurant data.
            </p>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleForgot} className="space-y-4">
            <p className="text-sm leading-relaxed text-black/70">
              Type the email you signed up with. We'll email you a link to set
              a new password.
            </p>
            <FieldEmail value={email} onChange={setEmail} />
            <button
              type="submit"
              disabled={submitting}
              className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Sending\u2026' : 'Send reset link'}
            </button>
            <button
              type="button"
              onClick={() => switchMode('signin')}
              className="block w-full text-center text-sm font-medium text-blue-700 hover:underline"
            >
              Back to sign in
            </button>
          </form>
        )}

        <div className="mt-8 flex justify-center gap-4 text-xs text-black/40">
          <a href="/pricing" className="hover:text-black/70 hover:underline">Pricing</a>
          <span aria-hidden="true">·</span>
          <a href="/privacy" className="hover:text-black/70 hover:underline">Privacy</a>
          <span aria-hidden="true">·</span>
          <a href="/terms" className="hover:text-black/70 hover:underline">Terms</a>
        </div>
      </main>
    </div>
  )
}

function FieldEmail({ value, onChange }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium" htmlFor="login-email">Email</label>
      <input
        id="login-email"
        type="email"
        inputMode="email"
        autoComplete="email"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="you@restaurant.com"
        className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
      />
    </div>
  )
}

function FieldPassword({ value, onChange, autoComplete, label }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium" htmlFor="login-password">{label}</label>
      <input
        id="login-password"
        type="password"
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
      />
    </div>
  )
}
